import DataPlugin from "./DataPlugin";
import HTMLPlugin from "./HTMLPlugin";
import Boot from "../../scenes/Boot";
import TrackingPlugin from "./TrackingPlugin";
import PlaceholderPlugin from "./PlaceholderPlugin";
import PostEffectPlugin from "./PostEffectPlugin";
import BaseScene from "../../scenes/BaseScene";

/**
 * a collection of plugins all accessible via scene.sponge (when extending base scene.)
 * @category plugins
 * @export
 * @class SpongePlugin
 * @extends {Phaser.Plugins.BasePlugin}
 */
export default class SpongePlugin extends Phaser.Plugins.BasePlugin {
  /**
   * @constructor Creates an instance of the Sponge plugin that just has easy access to all our global plugins.
   * (data controller, Scale Manager, HTML tools and Math / String Utils)
   * @param {Phaser.Plugins.PluginManager} pluginManager
   * @memberof GameData
   */
  constructor(pluginManager: Phaser.Plugins.PluginManager) {
    super(pluginManager);

    this.game = pluginManager.game;

    console.groupCollapsed("Sponge::plugins");

    // we might need this in the boot / controller class eventually but adding here for now so its in one place.
    pluginManager.install("_data", DataPlugin, true, "_data");
    this.data = pluginManager.get("_data") as DataPlugin;

    // boot up out HTMLUtils plugin and make it accessible, this is used for popups, forms as well as other non canvas / webGL content.
    pluginManager.install("_html", HTMLPlugin, true, "_html");
    this.html = pluginManager.get("_html") as HTMLPlugin;
    this.popup = this.html;

    // boot up out Placeholder plugin, used for creating temp sprite / images for prototyping.
    pluginManager.install(
      "_placeholder",
      PlaceholderPlugin,
      true,
      "_placeholder"
    );
    this.placeholder = pluginManager.get("_placeholder") as PlaceholderPlugin;

    // we might need this in the boot / controller class eventually but adding here for now so its in one place.
    pluginManager.install("_tracking", TrackingPlugin, true, "_tracking");
    this.tracking = pluginManager.get("_tracking") as TrackingPlugin;

    // Effects plugin
    pluginManager.install("_effects", PostEffectPlugin, true, "_effects");
    this.effects = pluginManager.get("_effects") as PostEffectPlugin;

    console.groupEnd();
  }

  /**
   * Callback when the game has finished booting.
   *
   * @param {Boot} bootState
   * @memberof Sponge
   */
  public postBoot(bootState: Boot): void {
    // save a reference
    this._boot = bootState;
    this.data.prepareDataModel();


    this.effects.startUp(this);
  }

  /**
   * a reference to our game.
   *
   * @type {Phaser.Game}
   * @memberof Sponge
   */
  public game: Phaser.Game;

  /**
   * access to data and tracking functions
   *
   * @type {DataPlugin}
   * @memberof Sponge
   */
  public data: DataPlugin;

  /**
   * instance of the placeholder image / sprite generation
   *
   * @type {PlaceholderPlugin}
   * @memberof SpongePlugin
   */
  public placeholder: PlaceholderPlugin;

  /**
   * Tracking functionallity (Offline Storage, SCORM and other tecnology syncronisation)
   *
   * @type {TrackingPlugin}
   * @memberof SpongePlugin
   */
  public tracking: TrackingPlugin;

  /**
   * access to HTML, popups and forms
   *
   * @type {HTMLPlugin}
   * @memberof Sponge
   */
  public html: HTMLPlugin;

  /**
   * an alias to the HTML plugin, (popups & forms)
   *
   * @type {HTMLPlugin}
   * @memberof SpongePlugin
   */
  public popup: HTMLPlugin;

  /**
   * our post effect controller.
   *
   * @type {PostEffectsPlugin}
   * @memberof SpongePlugin
   */
  public effects: PostEffectPlugin;

  /**
   * internal reference to bootstate if required.
   *
   * @private
   * @type {Boot}
   * @memberof SpongePlugin
   */
  private _boot: Boot | undefined;

  /**
   * get the current bootstate.
   *
   * @readonly
   * @type {Boot}
   * @memberof SpongePlugin
   */
  get bootState(): Boot | undefined {
    return this._boot;
  }

  getStoryline(key: string): string {
    console.log(`getting string of key '${key}' from storyline`);

    let ret = undefined;

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      ret = parent.GetPlayer().GetVar(key);
    } catch (e) {
      alert(`error getting string of key '${key}' from storyline`);
    }
    return ret;
  }

  setStoryline(key: string, value: string): void {
    console.log(
      `setting string of key '${key}' to value '${value}' from storyline`
    );

    try {
      // eslint-disable-next-line @typescript-eslint/ban-ts-ignore
      // @ts-ignore
      parent.GetPlayer().SetVar(key, value);
    } catch (e) {
      alert(
        `error setting string of key '${key}' to value '${value}' from storyline`
      );
    }
  }


  currentVO: Phaser.Sound.BaseSound | null = null;

  startVO(id: string, scene: BaseScene){

    console.log("PLAY VO", id,scene)

    const PlayingVO = this.data.save.voEnabled;
 
    //ignore if we have VO muted.
    if(!PlayingVO){
      return;
    }


    //if the sound has already been loaded
    if(scene.cache.audio.exists("VO-"+id)){
      //once loaded lets stop any currently playing VO
      this.stopVO();

      //add the sound to the scene
      this.currentVO = scene.sound.add("VO-"+id);
      //play the vo
      this.currentVO.play({loop:false});

      return
    }

    //we are going to load audio on demand
    //set audio to load VO file
    scene.load.audio("VO-"+id,[`assets/voice_over/${id}.mp3`,`assets/voice_over/${id}.ogg`])   // add task

    //setup our callback for when the audio file loads.
    scene.load.once('complete', ()=> {

      //once loaded lets stop any currently playing VO
      this.stopVO();

      //add the sound to the scene
      this.currentVO = scene.sound.add("VO-"+id);
      //play the vo
      this.currentVO.play({loop:false});

      }); 

      //initiate the load.
      scene.load.start();

  
  }

  stopVO(){
    if(this.currentVO){
      //if there is a currently playing VO lets stop it and free from memory.
      try {
      this.currentVO.stop();
      } catch(e){
        console.log("could not stop sound",e);
      }

      //nullify
      this.currentVO = null;
    }
  }
}
